import type { DraggingOverride, DrawingCoordinates, Point, ChartLayout, ChartPriceRange } from "./types";
import type { ZoomRange } from "features/chart";
import { yToPrice, xToTimestamp } from "./drawingDragHelpers";

type DragMoveResult = { override: DraggingOverride; coords: DrawingCoordinates };
type PointKey = "point1" | "point2" | "point3" | null;

export function computeDragMove(
  dragging: DraggingOverride,
  pointKey: PointKey,
  x: number, y: number,
  layout: ChartLayout,
  range: ChartPriceRange | null,
  zoom: ZoomRange,
  firstVisibleMs: number,
  timeframeMs: number,
): DragMoveResult | null {
  if (dragging.kind === "price") {
    if (!range) return null;
    const price = yToPrice(y, layout, range);
    return { override: { kind: "price", id: dragging.id, price }, coords: { kind: "single-price", price } };
  }

  if (dragging.kind === "timestamp") {
    const timestamp = xToTimestamp(x, layout, zoom, firstVisibleMs, timeframeMs);
    return {
      override: { kind: "timestamp", id: dragging.id, timestamp },
      coords: { kind: "single-timestamp", timestamp },
    };
  }

  if (dragging.kind === "twoPoint") {
    if (!range) return null;
    const updated: Point = {
      timestamp: xToTimestamp(x, layout, zoom, firstVisibleMs, timeframeMs),
      price: yToPrice(y, layout, range),
    };
    const point1 = pointKey === "point1" ? updated : dragging.point1;
    const point2 = pointKey === "point2" ? updated : dragging.point2;
    return {
      override: { kind: "twoPoint", id: dragging.id, point1, point2 },
      coords: { kind: "two-point", point1, point2 },
    };
  }

  if (dragging.kind === "threePoint") {
    if (!range) return null;
    const updated: Point = {
      timestamp: xToTimestamp(x, layout, zoom, firstVisibleMs, timeframeMs),
      price: yToPrice(y, layout, range),
    };
    let { point1, point2, point3 } = dragging;
    if (pointKey === "point3") {
      point3 = { ...point3, price: updated.price };
    } else {
      const thickness = dragging.point3.price - (dragging.point1.price + dragging.point2.price) / 2;
      if (pointKey === "point1") point1 = updated;
      if (pointKey === "point2") point2 = updated;
      point3 = { ...point3, price: thickness + (point1.price + point2.price) / 2 };
    }
    return {
      override: { kind: "threePoint", id: dragging.id, point1, point2, point3 },
      coords: { kind: "three-point", point1, point2, point3 },
    };
  }

  return null;
}
